import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { EmiCalculator } from "@/components/calculators/EmiCalculator";
import { HomeLoanEligibility } from "@/components/calculators/HomeLoanEligibility";
import { TdsCalculator } from "@/components/calculators/TdsCalculator";
import { GstCalculator } from "@/components/calculators/GstCalculator";
import { CostSheet } from "@/components/calculators/CostSheet";
import { CapitalGains } from "@/components/calculators/CapitalGains";
import { RoiCalculator } from "@/components/calculators/RoiCalculator";
import { RentalYieldCalculator } from "@/components/calculators/RentalYieldCalculator";
import { SipVsRealEstate } from "@/components/calculators/SipVsRealEstate";
import { RentVsBuy } from "@/components/calculators/RentVsBuy";
import { WbStampDuty } from "@/components/calculators/WbStampDuty";
import { PropertyComparison } from "@/components/calculators/PropertyComparison";
import { Calculator, Landmark, Receipt, Percent, FileSpreadsheet, TrendingUp, PiggyBank, KeyRound, BarChart3, Scale, Stamp, GitCompare } from "lucide-react";

const TABS = [
  { id: "compare", label: "Compare", icon: GitCompare, C: PropertyComparison, group: "Plan & Compare" },
  { id: "cost", label: "Cost Sheet", icon: FileSpreadsheet, C: CostSheet, group: "Plan & Compare" },
  { id: "rvb", label: "Rent vs Buy", icon: Scale, C: RentVsBuy, group: "Plan & Compare" },
  { id: "emi", label: "EMI", icon: Calculator, C: EmiCalculator, group: "Affordability" },
  { id: "elig", label: "Eligibility", icon: Landmark, C: HomeLoanEligibility, group: "Affordability" },
  { id: "roi", label: "ROI", icon: BarChart3, C: RoiCalculator, group: "Investment" },
  { id: "yield", label: "Rental Yield", icon: KeyRound, C: RentalYieldCalculator, group: "Investment" },
  { id: "sip", label: "SIP vs Property", icon: PiggyBank, C: SipVsRealEstate, group: "Investment" },
  { id: "tds", label: "TDS", icon: Receipt, C: TdsCalculator, group: "Tax & Charges" },
  { id: "gst", label: "GST", icon: Percent, C: GstCalculator, group: "Tax & Charges" },
  { id: "wb", label: "Stamp Duty (WB)", icon: Stamp, C: WbStampDuty, group: "Tax & Charges" },
  { id: "cg", label: "Capital Gains", icon: TrendingUp, C: CapitalGains, group: "Tax & Charges" },
];

const GROUPS = ["Plan & Compare", "Affordability", "Investment", "Tax & Charges"];

export default function Tools() {
  const [params] = useSearchParams();
  const initial = TABS.some((t) => t.id === params.get("tab")) ? params.get("tab") : "emi";
  const [active, setActive] = useState(initial);
  const Active = TABS.find((t) => t.id === active).C;

  return (
    <div className="pt-32">
      <div className="container-px mx-auto max-w-[1600px] pb-24">
        <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-cyan">Buyer Toolkit</p>
        <h1 className="font-display text-5xl font-extrabold uppercase leading-none text-foreground md:text-8xl">Calculators</h1>
        <p className="mt-6 max-w-xl text-muted-foreground">Twelve precise, bank-grade tools — including a smart property comparison. No sign-up, no catch — just run your numbers.</p>

        <div className="mt-12 grid gap-10 lg:grid-cols-[240px_1fr]">
          {/* Mobile: sticky horizontal chip strip. Desktop: grouped vertical list. */}
          <div data-testid="tools-tabs">
            {/* Mobile strip */}
            <div className="sticky top-16 z-20 -mx-4 border-y border-border bg-background/95 px-4 py-3 backdrop-blur lg:hidden">
              <div className="-mx-1 flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setActive(t.id)}
                    data-testid={`tab-${t.id}`}
                    className={`shrink-0 snap-start whitespace-nowrap rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                      active === t.id
                        ? "border-foreground bg-foreground text-background"
                        : "border-border bg-background text-muted-foreground hover:border-foreground hover:text-foreground"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <t.icon className="h-3.5 w-3.5" strokeWidth={1.5} /> {t.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Desktop grouped list */}
            <div className="hidden lg:block">
              {GROUPS.map((grp) => (
                <div key={grp} className="mb-6">
                  <p className="px-1 pb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan">{grp}</p>
                  <div className="flex flex-col gap-px border border-border bg-border">
                    {TABS.filter((t) => t.group === grp).map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setActive(t.id)}
                        data-testid={`tab-desk-${t.id}`}
                        className={`flex items-center gap-3 px-5 py-4 text-left font-mono text-xs uppercase tracking-widest transition-colors ${
                          active === t.id
                            ? "bg-cyan text-white"
                            : "bg-background text-muted-foreground hover:bg-card hover:text-foreground"
                        }`}
                      >
                        <t.icon className="h-4 w-4" strokeWidth={1.5} /> {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-border bg-card p-6 md:p-10">
            <AnimatePresence mode="wait">
              <motion.div key={active} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                <Active />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
